import {Injectable, OnDestroy} from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  of,
  Subscription,
  throwError,
  tap,
  pipe,
  Subject,
  debounceTime,
  startWith
} from 'rxjs';
import * as CryptoJS from "crypto-js";
import {map, catchError, switchMap, finalize, filter, take} from 'rxjs/operators';
import {IResultVerify, IUser, UserModel} from "../models/user.model";
import {AuthModel, IAuth} from "../models/auth.model";
import {AuthHTTPService} from './auth-http';
import {environment} from 'src/environments/environment';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {IOTPModel, OtpModel} from "../models/otp.model";
import * as moment from 'moment';
import {EncryptService} from "../../shared/services/encrypt.service";
import {ModuleModel} from "../../../models/security/module.model";
import {IResultInvitations, ResultInvitations} from "../../../models/security/create-user.model";
import {IJoinRequestPayload} from "../../../models/security/join-request.model";
import {PracticeService} from 'src/app/services/accounts/practice.service';
import {PracticeModel} from "../../../models/accounts/practice.model";

const jwtHelper = new JwtHelperService();
export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageAuth = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  private authStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}-token`;
  practices: PracticeModel[];
  selectedPractice: PracticeModel;
  subjectSearchPractice: Subject<string> = new Subject();
  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  limit = 100;
  offset = 0;
  private isRefreshingToken = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  setLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private encryptService: EncryptService,
    private router: Router,
    private practiceService: PracticeService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    // La carga inicial del usuario la hace el APP_INITIALIZER (app.module.ts);
    // llamar getUserByToken() aquí también duplicaba el GET a /auth/user/.
  }

  OnInit() {
    this.setLoading(false);
  }

  // public methods
  login(email: string, password: string, token: string): Observable<any> {
    this.setLoading(true);
    return this.authHttpService.login(email, password, token).pipe(
      map((obj: IOTPModel) => {
        this.setSessionKey("csrf-token", obj?.csrf_token || '');
        return OtpModel.fromObject(obj);
      }),
      map((auth: OtpModel) => {
        let vtoken = auth?.token || null;
        this.setStoreToken(vtoken || '');
        auth.token="";
        return this.setTokenStorage(auth) && vtoken;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(err);
      }),
      finalize(() => this.setLoading(false))
    );
  }

  getEmailInvitation(email: string): Observable<ResultInvitations> {
    return this.authHttpService
      .getEmailInvitation(email)
      .pipe(
        map((result: IResultInvitations) => ResultInvitations.fromObject(result))
      );
  }

  generateJoinRequest(payload: IJoinRequestPayload): Observable<any> {
    return this.authHttpService.generateJoinRequest(payload);
  }

  logout() {
    this.setLoading(true);
    return this.authHttpService
      .logout()
      .pipe(
        map(() => {
          localStorage.removeItem(this.authLocalStorageAuth);
          sessionStorage.clear();
          this.setLoading(false);
          window.location.href = '/auth/login';
        }),
        catchError((err) => {
          this.setLoading(false);
          console.error('err', err);
          return of(undefined);
        }),
        finalize(() => this.setLoading(false))
      )
  }

   getMenus(practiceId: string): Observable<ModuleModel[]> {
    return this.subjectSearchPractice.pipe(
      startWith(''),
      debounceTime(500),
      switchMap(search => this.practiceService.getPractices({ 'search': '' }, this.limit, this.offset)),
      tap(data => {
        this.practices = data.results;
        const selectedPractice = data.results && data.results.length > 0
          ? data.results.find(t => t?.id?.toString() === localStorage.getItem("practiceId")) || data.results[0]
          : null;

        if (selectedPractice) {

          localStorage.setItem("practiceName", selectedPractice?.name || '');
          localStorage.setItem("practiceId", selectedPractice?.id?.toString() || '');
        }
      }),
      catchError(error => {
        localStorage.setItem("practiceName", '');
        localStorage.setItem("practiceId", '');
        return of([]);
      }),
      switchMap(() => this.authHttpService.getMenus(localStorage.getItem("practiceId") || practiceId)),
      map(menus => ModuleModel.fromObjectArray(menus, true)),
      catchError(error => {
        return of([]);
      })
    );
  }

  checkUserAccess(allowedPath: string): Observable<boolean> {
    var practiceId = localStorage.getItem("practiceId") || '';
    return this.getMenus(practiceId).pipe(
      map(menus => menus.some(menu =>
        menu.menus?.some(submenu =>
          submenu.options?.some(option =>
            option.link === allowedPath
          )
        )
      )),
      tap(hasAccess => {
        if (!hasAccess) {
          this.router.navigate(['/404']);
        }
      }),
      catchError(error => {
        console.error('Error fetching menus:', error);
        return of(false);
      })
    );
  }

  isSuperUser(): boolean {
    if (this.currentUserValue) {
      return this.currentUserValue.isSuperUser;
    }
    return false;
  }

  isVerified(): boolean {
    if (this.currentUserValue) {
      return this.currentUserValue.isVerified;
    }
    return false;
  }

  isCreateAccountCompleted(): boolean {
    if (this.currentUserValue) {
      return this.currentUserValue.isVerified || this.currentUserValue.isRequestVerifiedPending;
    }
    return false;
  }

  get isBusiness(): boolean {
    if (this.currentUserValue) {
      return this.currentUserValue.isBusiness;
    }
    return false;
  }

  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageAuth, this.encryptService.encrypt(JSON.stringify(auth)));
      return true;
    }
    return false;
  }

  setSessionKey(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  private base64UrlDecode(base64UrlString: string): string {
    let base64String = base64UrlString.replace(/-/g, '+').replace(/_/g, '/');
    while (base64String.length % 4 !== 0) {
      base64String += '=';
    }
    return base64String;
  }

  private base64UrlEncode(base64String: string): string {
    return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }


  setStoreRefreshToken(refreshToken: string): void {
    this.setSessionKey("refreshToken", refreshToken);

  }

  setStoreToken(token: string): void {
    this.setSessionKey("token", token);
  }

  private setTokenStorage(auth: OtpModel): boolean {
    if (auth) {
      const data = this.encryptService.encrypt(JSON.stringify(auth));
      localStorage.setItem(this.authStorageToken, data);
      const loginMethod = (auth?.loginMethod === 'OTP' || auth?.loginMethod === 'TRADITIONAL')
        ? 'OTP' : auth.loginMethod;

      localStorage.setItem('loginMethod', loginMethod);
      localStorage.setItem("resendOtpDelay", auth.resendOtpDelay?.toString() || '10');

      return true;
    }
    return false;
  }

  getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const data = localStorage.getItem(this.authLocalStorageAuth) as string;
      if (!data) {
        return undefined;
      }
      const lsValue = this.encryptService.decrypt(data);
      if (!lsValue) {
        return undefined;
      }

      return JSON.parse(lsValue);
    } catch (error) {
      localStorage.removeItem(this.authLocalStorageAuth);
      sessionStorage.clear();
      console.error(error);
      return undefined;
    }
  }


  getSessionKey(key: string): string | null {
    return  sessionStorage.getItem(key)
    /*const urlSafeValue = sessionStorage.getItem(key);
    if (!urlSafeValue) {
      return null;
    }

    const base64Value = this.base64UrlDecode(urlSafeValue);

    const decryptedBytes = CryptoJS.AES.decrypt(base64Value, environment.refreshTokenSecret);
    return decryptedBytes.toString(CryptoJS.enc.Utf8);*/
  }



  getUserByToken(reintento: boolean = false): Observable<any> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.setLoading(true);

    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((obj: IUser) => UserModel.fromObject(obj)),
      tap((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.logout();
        }
      }),
      catchError((err) => {
        if (err?.status === 401 && !reintento) {
          const refreshToken = this.getSessionKey("refreshToken");

          if (!refreshToken) {
            this.logout();
            return of(undefined);
          }

          if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            this.refreshTokenSubject.next(null);

            return this.authHttpService.refreshToken(refreshToken).pipe(
              tap((newToken: any) => {
                const newIotpModel = {
                  token: newToken.access,
                  token_expiry_time: newToken.access_expiration,
                  otp_expiry_time: newToken.otp_expiration_time || '',
                  is_phone_verified: newToken.is_phone_verified || false,
                  resend_otp_delay: newToken.resend_otp_delay || 0,
                  login_method: newToken.login_method || 'default',
                  csrf_token:''
                };
                const otpModel = OtpModel.fromObject(newIotpModel);
                this.setTokenStorage(otpModel);

                const authModel: AuthModel = {
                  authToken: otpModel.token,
                  expiresIn: otpModel.optExpiryTime,
                  user: auth.user, setAuth(auth: AuthModel) {
                  }
                };

                this.setAuthFromLocalStorage(authModel);


                this.setStoreRefreshToken(newToken.refresh);
                this.refreshTokenSubject.next(newToken.access);
              }),
              catchError((refreshError) => {
                console.error('Error al refrescar el token:', refreshError);
                this.logout();
                return of(undefined);
              }),
              finalize(() => {
                this.isRefreshingToken = false;
              })
            );
          } else {
            return this.refreshTokenSubject.pipe(
              filter((token) => token !== null),
              take(1),
              switchMap(() => this.getUserByToken(true))
            );
          }
        }

        return of(undefined);
      }),
      finalize(() => this.setLoading(false))
    );
  }
  // need create new user then login
  registration(firstName: string, lastName: string, password: string, password2: string, email: string): Observable<any> {
    return this.authHttpService
      .registration(firstName, lastName, password, password2, email)
      .pipe(
        map((result: any) => result)
        //map((practice: IRequestVerificationModel) => RequestVerificationModel.fromObject(practice))
      );


  }

  forgotPassword(email: string): Observable<any> {
    this.setLoading(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.setLoading(false)));
  }

  forgotPasswordConfirm(token: string, newPassword1: string, newPassword2: string, otp: string): Observable<any> {
    this.setLoading(true);
    return this.authHttpService
      .forgotPasswordConfirm(token, newPassword1, newPassword2, otp)
      .pipe(finalize(() => this.setLoading(false)));
  }

  checkEmailExist(email: string): Observable<boolean> {
    this.setLoading(true);
    return this.authHttpService
      .verifyEmailExist(email)
      .pipe(
        map((result: IResultVerify) => {
          return result.success
        })
      );
  }

  checkUsernameExist(username: string): Observable<boolean> {
    this.setLoading(true);
    return this.authHttpService
      .verifyUsernameExist(username)
      .pipe(
        map((result: IResultVerify) => {
          return result.success
        })
      );
  }

  private getOTPTokenStorage(): OtpModel | undefined {
    try {
      const data = localStorage.getItem(this.authStorageToken) as string;
      const otpData = this.encryptService.decrypt(data);
      if (!otpData) {
        return undefined;
      }
      return JSON.parse(otpData);
    } catch (error) {
      return undefined;
    }
  }

  getOptExpire(): moment.Moment | undefined {
    let token: OtpModel | undefined = this.getOTPTokenStorage();
    if (token) {
      return moment(token.optExpiryTime);
    }
    return;
  }

  isOptTokenExpired(): boolean {
    let tokenExpiryTime: moment.Moment | undefined = this.getOptExpire();
    if (tokenExpiryTime) {
      let now = moment.now();
      if (tokenExpiryTime.diff(now) > 0) {
        return false;
      }
    }
    return true;
  }

  getOptToken(): string | undefined {
    return this.getSessionKey("token") ||'';
    /*let token = this.getOTPTokenStorage();
    if (token) {
      return token.token;
    }
    return;*/
  }

  getPhoneVerified(): boolean {
    let numberVerified: OtpModel | undefined = this.getOTPTokenStorage();
    if (numberVerified) {
      return numberVerified.isPhoneVerified;
    }
    return false;
  }

  requestVerificationCode(otp: string, method:string): Observable<any> {
    this.setLoading(true);
    let otpToken = this.getOptToken();
    if (otpToken == undefined) return new Observable();
    localStorage.removeItem(this.authLocalStorageAuth)
    return this.authHttpService
      .requestVerificationCode(otpToken, otp, method)
      .pipe(
        map((obj: IAuth) => {
          localStorage.removeItem(this.authStorageToken);
          this.setSessionKey('refreshToken',obj.refresh);
          this.setSessionKey('token',obj.access);
          return AuthModel.fromObject(obj);
        }),
        map((auth: AuthModel) => {
          return this.setAuthFromLocalStorage(auth);
        }),
        switchMap(() => this.getUserByToken()),
        catchError((err) => {
          console.error('err', err);
          return of(undefined);
        }),
        finalize(() => this.setLoading(false))
      );
  }


  resendVerificationCodeWithOptions(options: any = {}): Observable<any> {
    const token = this.getOptToken();
    if (token) {
      return this.authHttpService.resendVerificationCodeWithOptions(token, options);
    } else {
      return throwError('Verification token not found');
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  changePassword(new_password1: string, new_password2: string, oldPassword: string): Observable<any> {
    return this.authHttpService.changePassword(new_password1, new_password2, oldPassword);
  }
}
